import BarChart from "../chart/BarChart";
import LineChart from "../chart/LineChart";
import {useEffect, useState} from "react";
import {
    getSalesChartData,
    getCustomersChartData,
    getAverageTicketChartData,
    getProductSaleValueChartData,
    getProductSaleCountChartData,
    getProductSaleAverageTicketChartData,
    getServiceValueChartData,
    getServiceCountChartData,
    getServiceAverageTicketChartData,
    getAutomotiveServiceValueChartData,
    getAutomotiveServiceCountChartData,
    getAutomotiveServiceAverageTicketChartData,
} from "../../utils/requestUtils";
import {getYearMonthFormat, parseLocalDate, CompanyPermission, UserPermission} from "../../utils/utils";
import {usePermission} from "../../core/PermissionContext";

function useChartData(fetcher, startDate, endDate) {
    const [labels, setLabels] = useState([]);
    const [values, setValues] = useState([]);
    const [avgValues, setAvgValues] = useState([]);

    useEffect(() => {
        if (!fetcher) return;
        fetcher(startDate, endDate).then(data => {
            if (!data || data.length === 0) return;
            setLabels(data.map(d => getYearMonthFormat(parseLocalDate(d.date))));
            const vals = data.map(d => d.value);
            setValues(vals);
            const avg = vals.reduce((s, v) => s + v, 0) / vals.length;
            setAvgValues(vals.map(() => avg));
        });
    }, [fetcher]);

    return {labels, values, avgValues};
}

const ChartBlock = ({title, children}) => (
    <div style={{marginTop: '10px'}}>
        <div className="header-label">{title}</div>
        {children}
    </div>
);

const DashBoardPage = () => {
    const {hasCompanyPermission, hasUserPermission} = usePermission();

    const currentDate = new Date();
    const startDate = new Date(currentDate.getFullYear(), currentDate.getMonth() - 12, 1);
    const endDate = new Date(currentDate.getFullYear(), currentDate.getMonth() + 1, 0);

    const cp = (p) => hasCompanyPermission(p) && hasUserPermission(p);

    const showRentalValue = cp(CompanyPermission.VIEW_CHART_RENTAL_VALUE);
    const showRentalCount = cp(CompanyPermission.VIEW_CHART_RENTAL_COUNT);
    const showRentalTicket = cp(CompanyPermission.VIEW_CHART_RENTAL_TICKET);

    const showProductSaleValue = cp(CompanyPermission.VIEW_CHART_PRODUCT_SALE_VALUE);
    const showProductSaleCount = cp(CompanyPermission.VIEW_CHART_PRODUCT_SALE_COUNT);
    const showProductSaleTicket = cp(CompanyPermission.VIEW_CHART_PRODUCT_SALE_TICKET);

    const showServiceValue = cp(CompanyPermission.VIEW_CHART_SERVICE_VALUE);
    const showServiceCount = cp(CompanyPermission.VIEW_CHART_SERVICE_COUNT);
    const showServiceTicket = cp(CompanyPermission.VIEW_CHART_SERVICE_TICKET);

    const showAutomotiveValue = cp(CompanyPermission.VIEW_CHART_AUTOMOTIVE_VALUE);
    const showAutomotiveCount = cp(CompanyPermission.VIEW_CHART_AUTOMOTIVE_COUNT);
    const showAutomotiveTicket = cp(CompanyPermission.VIEW_CHART_AUTOMOTIVE_TICKET);

    const rentalValue = useChartData(showRentalValue ? getSalesChartData : null, startDate, endDate);
    const rentalCount = useChartData(showRentalCount ? getCustomersChartData : null, startDate, endDate);
    const rentalTicket = useChartData(showRentalTicket ? getAverageTicketChartData : null, startDate, endDate);

    const productSaleValue = useChartData(showProductSaleValue ? getProductSaleValueChartData : null, startDate, endDate);
    const productSaleCount = useChartData(showProductSaleCount ? getProductSaleCountChartData : null, startDate, endDate);
    const productSaleTicket = useChartData(showProductSaleTicket ? getProductSaleAverageTicketChartData : null, startDate, endDate);

    const serviceValue = useChartData(showServiceValue ? getServiceValueChartData : null, startDate, endDate);
    const serviceCount = useChartData(showServiceCount ? getServiceCountChartData : null, startDate, endDate);
    const serviceTicket = useChartData(showServiceTicket ? getServiceAverageTicketChartData : null, startDate, endDate);

    const automotiveValue = useChartData(showAutomotiveValue ? getAutomotiveServiceValueChartData : null, startDate, endDate);
    const automotiveCount = useChartData(showAutomotiveCount ? getAutomotiveServiceCountChartData : null, startDate, endDate);
    const automotiveTicket = useChartData(showAutomotiveTicket ? getAutomotiveServiceAverageTicketChartData : null, startDate, endDate);

    const hasAny = showRentalValue || showRentalCount || showRentalTicket ||
        showProductSaleValue || showProductSaleCount || showProductSaleTicket ||
        showServiceValue || showServiceCount || showServiceTicket ||
        showAutomotiveValue || showAutomotiveCount || showAutomotiveTicket;

    if (!hasAny) return null;

    return (
        <div className="dashboard-wrapper">
            {showRentalValue && (
                <ChartBlock title="Evolução do Faturamento - Aluguel">
                    <BarChart labels={rentalValue.labels} dataValues={rentalValue.values} currencyformat color={"#36A2EB66"}/>
                </ChartBlock>
            )}
            {showRentalCount && (
                <ChartBlock title="Evolução dos Aluguéis - Aluguel">
                    <BarChart labels={rentalCount.labels} dataValues={rentalCount.values} color={"#FF9F5E66"}/>
                </ChartBlock>
            )}
            {showRentalTicket && (
                <ChartBlock title="Evolução do Ticket Médio - Aluguel">
                    <LineChart labels={rentalTicket.labels} dataValues={rentalTicket.values} secondaryDataValues={rentalTicket.avgValues} label1="Ticket médio" label2="Média" currencyformat/>
                </ChartBlock>
            )}
            {showProductSaleValue && (
                <ChartBlock title="Evolução do Faturamento - Venda">
                    <BarChart labels={productSaleValue.labels} dataValues={productSaleValue.values} currencyformat color={"#36A2EB66"}/>
                </ChartBlock>
            )}
            {showProductSaleCount && (
                <ChartBlock title="Evolução das Vendas - Venda">
                    <BarChart labels={productSaleCount.labels} dataValues={productSaleCount.values} color={"#FF9F5E66"}/>
                </ChartBlock>
            )}
            {showProductSaleTicket && (
                <ChartBlock title="Evolução do Ticket Médio - Venda">
                    <LineChart labels={productSaleTicket.labels} dataValues={productSaleTicket.values} secondaryDataValues={productSaleTicket.avgValues} label1="Ticket médio" label2="Média" currencyformat/>
                </ChartBlock>
            )}
            {showServiceValue && (
                <ChartBlock title="Evolução do Faturamento - Serviço">
                    <BarChart labels={serviceValue.labels} dataValues={serviceValue.values} currencyformat color={"#36A2EB66"}/>
                </ChartBlock>
            )}
            {showServiceCount && (
                <ChartBlock title="Evolução dos Serviços - Serviço">
                    <BarChart labels={serviceCount.labels} dataValues={serviceCount.values} color={"#FF9F5E66"}/>
                </ChartBlock>
            )}
            {showServiceTicket && (
                <ChartBlock title="Evolução do Ticket Médio - Serviço">
                    <LineChart labels={serviceTicket.labels} dataValues={serviceTicket.values} secondaryDataValues={serviceTicket.avgValues} label1="Ticket médio" label2="Média" currencyformat/>
                </ChartBlock>
            )}
            {showAutomotiveValue && (
                <ChartBlock title="Evolução do Faturamento - Serviço Automotivo">
                    <BarChart labels={automotiveValue.labels} dataValues={automotiveValue.values} currencyformat color={"#36A2EB66"}/>
                </ChartBlock>
            )}
            {showAutomotiveCount && (
                <ChartBlock title="Evolução dos Serviços Automotivos - Serviço Automotivo">
                    <BarChart labels={automotiveCount.labels} dataValues={automotiveCount.values} color={"#FF9F5E66"}/>
                </ChartBlock>
            )}
            {showAutomotiveTicket && (
                <ChartBlock title="Evolução do Ticket Médio - Serviço Automotivo">
                    <LineChart labels={automotiveTicket.labels} dataValues={automotiveTicket.values} secondaryDataValues={automotiveTicket.avgValues} label1="Ticket médio" label2="Média" currencyformat/>
                </ChartBlock>
            )}
        </div>
    );
};

export default DashBoardPage;
